import { lazy, Suspense } from 'react';
import { organizationJsonLd } from '@/content/jsonld';
import { Hero } from '@/features/home/Hero';
import { DEFERRED_SELECTOR } from '@/features/home/useDeferredSection';
import { DEFAULT_TITLE, Seo } from '@/lib/seo';
import { websiteJsonLd } from '@/lib/seo/website';
import { whenNeeded } from '@/lib/hydration-gate';
import { usePublicSettings } from '@/lib/site/public-settings';

// Everything under the hero is its own chunk: the hero is the LCP and must not wait for the carousel,
// tabs and section styles. On the server (pre-render) the boundary resolves before the HTML is sent.
// In the browser the pre-rendered sections stay as they are — visible, styled, links working — until
// the person scrolls or touches the page, or the browser has been idle for a few seconds: only then is
// the chunk fetched and the sections hydrated (`lib/hydration-gate.ts`). A navigation *to* the home page
// has no server markup to protect and fetches the chunk at once.
const HomeSections = lazy(() =>
  whenNeeded({ markup: DEFERRED_SELECTOR }).then(() => import('@/features/home/HomeSections')),
);

interface HomeSeoCopy {
  title: string;
  description: string;
}

// Search copy (D-353): title 60 characters at most with the main phrase first; description 140–160 characters with a call to action.
const seo = {
  title: DEFAULT_TITLE,
  description:
    'Granite slabs, tiles and countertops from our factory in Rajsamand, Rajasthan. Black, red, white and pink granite. Try it in your room. Ask for a price.',
} satisfies HomeSeoCopy;

export function Component() {
  const copy = seo;
  const { phone, email, address, social } = usePublicSettings();
  return (
    <>
      <Seo
        title={copy.title}
        description={copy.description}
        path="/"
        // `Organization` becomes a `LocalBusiness` once the firm's address is in the settings
        jsonLd={[organizationJsonLd({ phone, email, address, social }), websiteJsonLd()]}
      />
      <Hero />
      {/* the attribute names are DEFERRED_SELECTOR and DEFERRED_ATTRIBUTE (features/home/useDeferredSection.ts) */}
      <div data-home-sections="" data-deferred="">
        <Suspense fallback={<div style={{ minHeight: '100svh' }} aria-hidden="true" />}>
          <HomeSections />
        </Suspense>
      </div>
    </>
  );
}
